import moment from 'moment-timezone';

const TIMEZONE = 'America/Sao_Paulo';

const MONTH_NAMES_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export type RestartCompetenceValue =
  | 'PREVIOUS_MONTH'
  | 'CURRENT_MONTH'
  | 'NEXT_MONTH';

export interface RestartBatchInput {
  status: string;
  isManualProcessing: boolean;
  itemCount: number;
}

export interface RestartBatchEligibility {
  allowed: boolean;
  reason?: string;
}

export interface RestartCompetenceOption {
  value: RestartCompetenceValue;
  label: string;
  startDate: string;
}

const RESTARTABLE_STATUSES = new Set(['OPENED', 'ERROR']);

export function canRestartBatch(
  batch: RestartBatchInput,
): RestartBatchEligibility {
  if (batch.isManualProcessing) {
    return {
      allowed: false,
      reason:
        'Lote em processamento manual. Aguarde a conclusão ou desative o modo manual.',
    };
  }

  if (!RESTARTABLE_STATUSES.has(batch.status)) {
    return {
      allowed: false,
      reason: 'Só é possível reiniciar lotes com status Aberto ou Erro.',
    };
  }

  if (batch.itemCount === 0) {
    return {
      allowed: false,
      reason: 'Lote sem itens ativos para reiniciar.',
    };
  }

  return { allowed: true };
}

export function getDefaultRestartCompetenceMonth(
  referenceDate: Date | string = new Date(),
): string {
  return moment
    .tz(referenceDate, TIMEZONE)
    .subtract(1, 'month')
    .startOf('month')
    .format('YYYY-MM-DD');
}

export function buildRestartCompetenceOptions(
  referenceDate: Date | string = new Date(),
): {
  default: RestartCompetenceValue;
  options: RestartCompetenceOption[];
} {
  const reference = moment.tz(referenceDate, TIMEZONE).startOf('day');
  const previous = reference.clone().subtract(1, 'month').startOf('month');
  const current = reference.clone().startOf('month');
  const next = reference.clone().add(1, 'month').startOf('month');

  const formatLabel = (monthMoment: moment.Moment, suffix: string) => {
    const monthName = MONTH_NAMES_PT[monthMoment.month()];
    return `${monthName}/${monthMoment.year()} — ${suffix}`;
  };

  return {
    default: 'PREVIOUS_MONTH',
    options: [
      {
        value: 'PREVIOUS_MONTH',
        label: formatLabel(previous, 'mês passado'),
        startDate: previous.format('YYYY-MM-DD'),
      },
      {
        value: 'CURRENT_MONTH',
        label: formatLabel(current, 'mês atual'),
        startDate: current.format('YYYY-MM-DD'),
      },
      {
        value: 'NEXT_MONTH',
        label: formatLabel(next, 'próximo mês'),
        startDate: next.format('YYYY-MM-DD'),
      },
    ],
  };
}

export function buildRestartConfirmationMessage(
  itemCount: number,
  competenceLabel: string,
): string {
  return (
    `Serão reiniciados ${itemCount} item(ns) com vigência em ${competenceLabel}. ` +
    'Ao confirmar: uma nova cotação/apólice mãe será criada na Yelum; ' +
    'os itens serão readicionados via add item; certificados atuais serão removidos; ' +
    'todos os itens voltarão para "Apólice a faturar"; ' +
    'os números de proposta, cotação e apólice serão resetados; ' +
    'e a transmissão deste mês será considerada efetuada.'
  );
}

export function summarizeRestartStatus(
  items: Array<{ status: string }>,
): { total: number; added: number; failed: number; pending: number } {
  const added = items.filter((item) => item.status === 'added').length;
  const failed = items.filter((item) => item.status === 'failed').length;
  return {
    total: items.length,
    added,
    failed,
    pending: items.length - added - failed,
  };
}
